// hooks/use-workspace-nodes.js — JOURNEY MAP DATA LAYER
// ═══════════════════════════════════════════════════════════
// Fetches and manages workspace_nodes for a single workspace.
// Responsibilities:
//   - Load all nodes on mount
//   - Auto-create the root node on first open (from the workspace goal)
//   - Provide createMomentNode() for the Haiku classifier to call
//   - Provide createFileNode() for useArtifacts to call
//   - Enforce the 20-file limit at the node-creation layer
//   - Expose a flat list of nodes ready for the journey map renderer
//
// Does NOT own:
//   - The map rendering (features/workstation-journey-map.js)
//   - The Haiku classification call (features/workstation-chat-panel.js
//     fires it via /api/workspace/classify-moment)
//   - The UI for file-limit errors (the chat panel shows a toast)
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useRef } from 'react';
import { NODE_TYPES, NODE_STATUS, WORKSPACE_FILE_LIMIT } from '../lib/workspace-config';

// ── Smart parent picker ────────────────────────────────────
// Returns the workspace ROOT node as the parent for ALL non-root
// nodes. The journey map renderer groups same-type siblings into
// angular wedges, so a flat tree (depth = 1) produces a clean
// type-clustered radial mindmap instead of a linear chain.
//
// Why flat: chaining same-type nodes creates straight lines in the
// radial layout because each child of a single-child parent inherits
// the parent's angle. A flat tree spreads everything around root and
// the type wedges in the renderer create the visual hierarchy.
function pickSmartParent(nodes, _type, _fileSource) {
  if (!nodes || nodes.length === 0) return null;
  const root = nodes.find(n => n.type === NODE_TYPES.ROOT);
  return root || null;
}

export function useWorkspaceNodes({ workspaceId, supabase, workspaceGoal }) {
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const rootCreatedRef = useRef(false);

  // ── Load all nodes for this workspace ─────────────────
  const loadNodes = useCallback(async () => {
    if (!workspaceId || !supabase) return;
    try {
      const { data, error: queryError } = await supabase
        .from('workspace_nodes')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: true });
      if (queryError) throw queryError;
      setNodes(data || []);
      setLoading(false);
      return data || [];
    } catch (e) {
      console.error('[use-workspace-nodes] load failed:', e.message);
      setError(e.message);
      setLoading(false);
      return [];
    }
  }, [workspaceId, supabase]);

  // ── Auto-create root node if workspace has none ──────
  useEffect(() => {
    if (!workspaceId || !supabase) return;
    let cancelled = false;

    (async () => {
      const existing = await loadNodes();
      if (cancelled) return;
      if (existing.length === 0 && !rootCreatedRef.current) {
        rootCreatedRef.current = true;
        try {
          const { data: rootNode, error: insertError } = await supabase
            .from('workspace_nodes')
            .insert({
              workspace_id: workspaceId,
              parent_id: null,
              type: NODE_TYPES.ROOT,
              title: workspaceGoal || 'Space',
              body: null,
              status: NODE_STATUS.ACTIVE,
            })
            .select()
            .single();
          if (insertError) throw insertError;
          if (!cancelled && rootNode) setNodes([rootNode]);
        } catch (e) {
          console.error('[use-workspace-nodes] root creation failed:', e.message);
          if (!cancelled) setError(e.message);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [workspaceId, supabase, workspaceGoal, loadNodes]);

  // ── Create a moment node ──────────────────────────────
  // Smart parenting: each new node attaches to the most recent
  // node OF THE SAME TYPE (decision → previous decision, question
  // → previous question, etc.). The first node of each type attaches
  // to the root. This produces a tree with one branch per type
  // instead of a single chronological chain.
  const createMomentNode = useCallback(async ({ type, title, body, chatMessageId }) => {
    if (!workspaceId || !supabase) return null;
    if (!Object.values(NODE_TYPES).includes(type)) {
      console.error('[use-workspace-nodes] invalid node type:', type);
      return null;
    }
    try {
      const parent = pickSmartParent(nodes, type, null);
      const { data: newNode, error: insertError } = await supabase
        .from('workspace_nodes')
        .insert({
          workspace_id: workspaceId,
          parent_id: parent?.id || null,
          type,
          title: title?.slice(0, 120) || 'Untitled',
          body: body?.slice(0, 500) || null,
          status: NODE_STATUS.ACTIVE,
          chat_message_id: chatMessageId || null,
        })
        .select()
        .single();
      if (insertError) throw insertError;
      setNodes(prev => [...prev, newNode]);
      return newNode;
    } catch (e) {
      console.error('[use-workspace-nodes] moment creation failed:', e.message);
      return null;
    }
  }, [workspaceId, supabase, nodes]);

  // ── Create a file node ────────────────────────────────
  // Called by useArtifacts.saveArtifact when a file is added to a
  // workspace session (either uploaded by the user or generated by
  // Shift). Enforces the 20-file limit.
  const createFileNode = useCallback(async ({ title, artifactId, fileSource, chatMessageId }) => {
    if (!workspaceId || !supabase) return { ok: false, reason: 'not_ready' };

    // Enforce the file limit
    const currentFileCount = nodes.filter(n => n.type === NODE_TYPES.FILE).length;
    if (currentFileCount >= WORKSPACE_FILE_LIMIT) {
      return {
        ok: false,
        reason: 'limit',
        message: `Space file limit reached (${WORKSPACE_FILE_LIMIT}). Remove an older file to add a new one.`,
      };
    }

    try {
      // Smart parenting for files: cluster by file_source so all
      // uploaded files chain together and all generated files chain
      // together. First file of each source attaches to the root.
      const parent = pickSmartParent(nodes, NODE_TYPES.FILE, fileSource);
      const { data: newNode, error: insertError } = await supabase
        .from('workspace_nodes')
        .insert({
          workspace_id: workspaceId,
          parent_id: parent?.id || null,
          type: NODE_TYPES.FILE,
          file_source: fileSource === 'generated' ? 'generated' : 'uploaded',
          artifact_id: artifactId || null,
          title: title?.slice(0, 120) || 'File',
          body: null,
          status: NODE_STATUS.ACTIVE,
          chat_message_id: chatMessageId || null,
        })
        .select()
        .single();
      if (insertError) throw insertError;
      setNodes(prev => [...prev, newNode]);
      return { ok: true, node: newNode };
    } catch (e) {
      console.error('[use-workspace-nodes] file creation failed:', e.message);
      return { ok: false, reason: 'error', message: e.message };
    }
  }, [workspaceId, supabase, nodes]);

  // ── Update a node's status (e.g., active → past) ─────
  const updateNodeStatus = useCallback(async (nodeId, status) => {
    if (!workspaceId || !supabase || !nodeId) return;
    try {
      const { error: updateError } = await supabase
        .from('workspace_nodes')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', nodeId);
      if (updateError) throw updateError;
      setNodes(prev => prev.map(n => n.id === nodeId ? { ...n, status } : n));
    } catch (e) {
      console.error('[use-workspace-nodes] status update failed:', e.message);
    }
  }, [workspaceId, supabase]);

  // ── Hard-delete a node ───────────────────────────────
  // Removes the node row entirely so it disappears from both the canvas
  // tile grid and the journey map. Children of the deleted node are
  // cascade-removed by the FK constraint (parent_id ON DELETE CASCADE).
  // We do NOT touch the artifacts table — the underlying artifact stays
  // in the user's library and can be re-added to a workspace later.
  const deleteNode = useCallback(async (nodeId) => {
    if (!workspaceId || !supabase || !nodeId) return false;
    try {
      const { error: deleteError } = await supabase
        .from('workspace_nodes')
        .delete()
        .eq('id', nodeId);
      if (deleteError) throw deleteError;
      // Drop the node and any descendant nodes from local state.
      // The FK cascade has already done the same on the server.
      setNodes(prev => {
        const removed = new Set([nodeId]);
        let grew = true;
        while (grew) {
          grew = false;
          prev.forEach(n => {
            if (n.parent_id && removed.has(n.parent_id) && !removed.has(n.id)) {
              removed.add(n.id);
              grew = true;
            }
          });
        }
        return prev.filter(n => !removed.has(n.id));
      });
      return true;
    } catch (e) {
      console.error('[use-workspace-nodes] delete failed:', e.message);
      return false;
    }
  }, [workspaceId, supabase]);

  // ── Cross-client sync for multi-user workspaces ────────
  // Two layers — both safe in Solo (which is single-user, so they're
  // no-ops there).
  //
  // 1) Supabase realtime postgres_changes on workspace_nodes. Fast
  //    (~200ms) but requires the table to be in the supabase_realtime
  //    publication. See docs/sql/usage-ledger.sql for the ALTER
  //    PUBLICATION statement that enables it.
  // 2) Lightweight 3s poll as a fallback. Always works, even if the
  //    realtime publication isn't configured. The merge dedupes by id
  //    so realtime + poll never produce duplicates.
  useEffect(() => {
    if (!workspaceId || !supabase) return;

    const mergeFromServer = (incoming) => {
      if (!incoming || incoming.length === 0) return;
      setNodes(prev => {
        const byId = new Map(prev.map(n => [n.id, n]));
        for (const n of incoming) byId.set(n.id, { ...byId.get(n.id), ...n });
        const next = Array.from(byId.values());
        // Drop any local nodes the server removed (only when the server
        // returned a complete list — which our poll always does).
        const incomingIds = new Set(incoming.map(n => n.id));
        return next.filter(n => incomingIds.has(n.id));
      });
    };

    // Realtime channel — fast path
    const channel = supabase
      .channel(`workspace_nodes:${workspaceId}`)
      .on('postgres_changes', {
        event:  'INSERT',
        schema: 'public',
        table:  'workspace_nodes',
        filter: `workspace_id=eq.${workspaceId}`,
      }, (payload) => {
        const newNode = payload.new;
        if (!newNode?.id) return;
        setNodes(prev => prev.some(n => n.id === newNode.id) ? prev : [...prev, newNode]);
      })
      .on('postgres_changes', {
        event:  'UPDATE',
        schema: 'public',
        table:  'workspace_nodes',
        filter: `workspace_id=eq.${workspaceId}`,
      }, (payload) => {
        const updated = payload.new;
        if (!updated?.id) return;
        setNodes(prev => prev.map(n => n.id === updated.id ? { ...n, ...updated } : n));
      })
      .on('postgres_changes', {
        event:  'DELETE',
        schema: 'public',
        table:  'workspace_nodes',
        filter: `workspace_id=eq.${workspaceId}`,
      }, (payload) => {
        const removedId = payload.old?.id;
        if (!removedId) return;
        setNodes(prev => prev.filter(n => n.id !== removedId));
      })
      .subscribe();

    // Poll fallback — runs every 2s. Cheap (single SELECT, ~10 rows).
    let pollActive = true;
    const poll = async () => {
      if (!pollActive) return;
      try {
        const { data } = await supabase
          .from('workspace_nodes')
          .select('*')
          .eq('workspace_id', workspaceId)
          .order('created_at', { ascending: true });
        if (pollActive && data) mergeFromServer(data);
      } catch (e) {
        // Silent — realtime is the primary path. Poll just keeps invitees in sync.
        console.debug('[use-workspace-nodes] poll failed:', e.message);
      }
    };
    const pollTimer = setInterval(poll, 2000);

    // Snap-refresh on focus / visibility — the invitee returning to
    // the tab sees the latest canvas state immediately.
    const onFocus = () => poll();
    const onVisible = () => { if (document.visibilityState === 'visible') poll(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      pollActive = false;
      clearInterval(pollTimer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
      supabase.removeChannel(channel);
    };
  }, [workspaceId, supabase]);

  // ── Computed: file count + limit status ──────────────
  const fileCount = nodes.filter(n => n.type === NODE_TYPES.FILE).length;
  const fileLimit = WORKSPACE_FILE_LIMIT;
  const filesAtLimit = fileCount >= fileLimit;

  return {
    nodes,
    loading,
    error,
    fileCount,
    fileLimit,
    filesAtLimit,
    loadNodes,
    createMomentNode,
    createFileNode,
    updateNodeStatus,
    deleteNode,
  };
}
